import { falloff, forEachInRadius, resample, sampleSource, levels, loadImage, clamp, tint, blur, lerp } from "../shared";

class PinArt {
  constructor(ctx) { this.ctx = ctx; this.writesColors = true; this.imp = new Float32Array(ctx.count); }
  resize() {
    const oN = Math.round(Math.sqrt(this.imp.length));
    this.imp = oN === this.ctx.N ? this.imp : resample(this.imp, oN, this.ctx.N);
    if (this.img) this.applyImage();
  }
  brushRadius() { return this.ctx.params.radius; }
  pointer(e) {
    if (e.type === "down") { this.last = e; this.stamp(e); }
    else if (e.type === "move" && this.last) {
      const dx = e.x - this.last.x, dz = e.z - this.last.z, d = Math.hypot(dx, dz), stepL = Math.max(0.03, this.ctx.params.radius * 0.25);
      const n = Math.min(40, Math.ceil(d / stepL));
      for (let k = 1; k <= n; k++) this.stamp({ ...e, x: this.last.x + (dx * k) / n, z: this.last.z + (dz * k) / n });
      this.last = e;
    } else if (e.type === "up") this.last = null;
  }
  changedSince(s) { const a = this.imp; if (s.length !== a.length) return true; for (let i = 0; i < a.length; i++) if (a[i] !== s[i]) return true; return false; }
  stamp({ x, z, pressure, shift }) {
    const P = this.ctx.params, N = this.ctx.N, imp = this.imp;
    const tool = shift ? (P.tool === "press" ? "lift" : "press") : P.tool;
    const p = clamp(pressure * P.pressure * this.ctx.g.force, 0, 1.5), r = P.radius, R = r * (1 + P.influence * 0.8);
    forEachInRadius(this.ctx, x, z, R, (i, d) => {
      let w = falloff(d / R, P.smooth);
      if (d > r) w *= lerp(1, P.influence, (d - r) / (R - r + 1e-6));
      const want = clamp(P.depth * p * w, 0, 1);
      if (tool === "press") { if (want > imp[i]) imp[i] += (want - imp[i]) * 0.6; }
      else if (tool === "lift") imp[i] = Math.max(0, imp[i] - want * 0.25);
      else {
        const c = i % N, rr = (i / N) | 0;
        const avg = (imp[c > 0 ? i - 1 : i] + imp[c < N - 1 ? i + 1 : i] + imp[rr > 0 ? i - N : i] + imp[rr < N - 1 ? i + N : i]) / 4;
        imp[i] += (avg - imp[i]) * Math.min(1, w * 0.8);
      }
    });
  }
  step() {
    const { target, colors } = this.ctx, imp = this.imp;
    for (let i = 0; i < imp.length; i++) { target[i] = 1 - 2 * imp[i]; tint(colors, i, imp[i], 0.75); }
  }
  applyImage() {
    const P = this.ctx.params, s = sampleSource(this.img, this.ctx.N); if (!s) return;
    const l = P.imgBlur > 0 ? blur(s.lum, this.ctx.N, P.imgBlur) : s.lum;
    for (let i = 0; i < l.length; i++) this.imp[i] = levels(l[i], P.imgBlack, P.imgWhite, P.imgGamma, P.imgInvert) * P.imgDepth;
  }
  onParams(key) { if (key.startsWith("img") && this.img) this.applyImage(); }
  async action(id, file) {
    if (id === "upload") { const img = await loadImage(file); this.ctx.pushHistory(); this.img = img; this.applyImage(); return "Image converted to impression"; }
    if (id === "clearImage") { this.ctx.pushHistory(); this.img = null; this.imp.fill(0); }
  }
  snapshot() { return this.imp.slice(); }
  restore(s) { if (s.length === this.imp.length) this.imp.set(s); }
  clear() { this.imp.fill(0); this.img = null; }
  telemetry() {
    let n = 0, mx = 0; for (const v of this.imp) { if (v > 0.02) n++; if (v > mx) mx = v; }
    return [["Pressed pins", n.toLocaleString()], ["Max depth", `${Math.round(mx * 100)}%`], ["Tool", this.ctx.params.tool.toUpperCase()], ["Image", this.img ? "LOADED" : "—"]];
  }
}

const pinArt = {
  id: "pin-art", name: "Pin Impression", icon: "Fingerprint", rest: 1, hasBrush: true,
  desc: "The original instrument. Press and drag across the board — spring-loaded pins sink according to distance and pressure and hold the impression like the physical toy. Shift-drag inverts the tool.",
  math: "depth = D · p · (1 − (d/r)²)^(0.5 + 3s)",
  params: [
    { key: "tool", label: "Tool", type: "select", options: [["press", "Press"], ["lift", "Lift"], ["smooth", "Smooth"]], def: "press" },
    { key: "radius", label: "Brush radius", type: "range", min: 0.15, max: 2.5, step: 0.05, def: 0.7, testid: "slider-brush-radius" },
    { key: "depth", label: "Impression depth", type: "range", min: 0.05, max: 1, step: 0.01, def: 0.65, testid: "slider-brush-depth" },
    { key: "pressure", label: "Pressure sensitivity", type: "range", min: 0.1, max: 2, step: 0.05, def: 1, testid: "slider-brush-pressure" },
    { key: "smooth", label: "Smoothness", type: "range", min: 0, max: 1, step: 0.01, def: 0.45, testid: "slider-brush-smoothness" },
    { key: "influence", label: "Spatial influence", type: "range", min: 0, max: 1, step: 0.01, def: 0.3 },
    { key: "imgInvert", label: "Light areas protrude", type: "toggle", def: false, section: "Image → depth" },
    { key: "imgBlack", label: "Dark point", type: "range", min: 0, max: 0.9, step: 0.01, def: 0.05, section: "Image → depth" },
    { key: "imgWhite", label: "Light point", type: "range", min: 0.1, max: 1, step: 0.01, def: 0.95, section: "Image → depth" },
    { key: "imgGamma", label: "Contrast curve", type: "range", min: 0.3, max: 3, step: 0.05, def: 1, section: "Image → depth" },
    { key: "imgDepth", label: "Relief depth", type: "range", min: 0.1, max: 1, step: 0.01, def: 0.85, section: "Image → depth" },
    { key: "imgBlur", label: "Soften", type: "range", min: 0, max: 4, step: 1, def: 1, section: "Image → depth" },
  ],
  actions: [
    { id: "upload", label: "Upload image", kind: "file", testid: "image-upload-input", section: "Image → depth" },
    { id: "clearImage", label: "Remove image", section: "Image → depth" },
  ],
  create: (ctx) => new PinArt(ctx),
};
export default pinArt;
